import React, { useEffect, useState, useCallback } from 'react';
import { Layout } from '../components/Layout';
import { useAuth } from '../context/AuthContext';
import { absenceAPI } from '../lib/api';
import AbsenceModal from '../components/AbsenceModal';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Plus, Loader2, Check, X, CalendarRange } from 'lucide-react';
import { toast } from 'sonner';

const STATUS_STYLES = {
  pending:  'bg-amber-100 text-amber-700 border-amber-200',
  approved: 'bg-green-100 text-green-700 border-green-200',
  rejected: 'bg-red-100 text-red-700 border-red-200',
};

export default function Absences() {
  const { user } = useAuth();
  const canApprove = ['owner', 'manager', 'super_admin'].includes(user?.role);
  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [modalOpen, setModalOpen] = useState(false);
  const [actingId, setActingId] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await absenceAPI.list();
      setRequests(data.requests || []);
    } catch {
      toast.error('Failed to load time-off requests');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const handleApprove = async (id) => {
    setActingId(id);
    try {
      await absenceAPI.approve(id);
      toast.success('Request approved');
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Approve failed');
    } finally {
      setActingId(null);
    }
  };

  const handleReject = async (id) => {
    setActingId(id);
    try {
      await absenceAPI.reject(id);
      toast.success('Request rejected');
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Reject failed');
    } finally {
      setActingId(null);
    }
  };

  return (
    <Layout>
      <div className="space-y-6">
        {/* Header */}
        <div className="flex items-start justify-between gap-4 flex-wrap">
          <div>
            <h1 className="text-2xl font-extrabold text-slate-900 flex items-center gap-2" style={{ fontFamily: 'Manrope' }}>
              <CalendarRange size={22} className="text-orange-500" />
              Absences
            </h1>
            <p className="text-sm text-slate-500 mt-1">
              Time-off requests and approvals.
            </p>
          </div>
          <Button
            className="bg-orange-500 hover:bg-orange-600 text-white"
            onClick={() => setModalOpen(true)}
            data-testid="absence-new-btn"
          >
            <Plus size={16} className="mr-2" />
            Request Time Off
          </Button>
        </div>

        {/* List */}
        <Card>
          <CardHeader>
            <CardTitle className="text-base">Requests</CardTitle>
          </CardHeader>
          <CardContent>
            {loading ? (
              <div className="py-12 flex justify-center">
                <Loader2 className="h-6 w-6 animate-spin text-slate-400" />
              </div>
            ) : requests.length === 0 ? (
              <p className="text-sm text-slate-500 text-center py-8" data-testid="absence-empty">
                No time-off requests yet.
              </p>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-sm" data-testid="absence-table">
                  <thead>
                    <tr className="text-left text-xs font-semibold text-slate-500 uppercase tracking-wide border-b">
                      {canApprove && <th className="py-2 pr-4">Employee</th>}
                      <th className="py-2 pr-4">Type</th>
                      <th className="py-2 pr-4">Start</th>
                      <th className="py-2 pr-4">End</th>
                      <th className="py-2 pr-4">Days</th>
                      <th className="py-2 pr-4">Note</th>
                      <th className="py-2 pr-4">Status</th>
                      {canApprove && <th className="py-2 pr-4 text-right">Actions</th>}
                    </tr>
                  </thead>
                  <tbody>
                    {requests.map((r) => (
                      <tr key={r.id} className="border-b last:border-0" data-testid={`absence-row-${r.id}`}>
                        {canApprove && <td className="py-3 pr-4 text-slate-700">{r.employee_name || '—'}</td>}
                        <td className="py-3 pr-4 text-slate-700">{r.leave_type_name}</td>
                        <td className="py-3 pr-4 text-slate-700">{r.start_date}</td>
                        <td className="py-3 pr-4 text-slate-700">{r.end_date}</td>
                        <td className="py-3 pr-4 text-slate-700">{r.days}</td>
                        <td className="py-3 pr-4 text-slate-500 max-w-[200px] truncate">{r.note || '—'}</td>
                        <td className="py-3 pr-4">
                          <Badge variant="outline" className={`${STATUS_STYLES[r.status] || ''} border`}>
                            {r.status}
                          </Badge>
                        </td>
                        {canApprove && (
                          <td className="py-3 pr-4 text-right">
                            {r.status === 'pending' ? (
                              <div className="flex justify-end gap-2">
                                <Button
                                  size="sm"
                                  variant="outline"
                                  disabled={actingId === r.id}
                                  onClick={() => handleApprove(r.id)}
                                  data-testid={`absence-approve-${r.id}`}
                                  className="text-green-700 border-green-200 hover:bg-green-50"
                                >
                                  <Check size={14} className="mr-1" />
                                  Approve
                                </Button>
                                <Button
                                  size="sm"
                                  variant="outline"
                                  disabled={actingId === r.id}
                                  onClick={() => handleReject(r.id)}
                                  data-testid={`absence-reject-${r.id}`}
                                  className="text-red-700 border-red-200 hover:bg-red-50"
                                >
                                  <X size={14} className="mr-1" />
                                  Reject
                                </Button>
                              </div>
                            ) : (
                              <span className="text-xs text-slate-400">—</span>
                            )}
                          </td>
                        )}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      <AbsenceModal open={modalOpen} onClose={() => setModalOpen(false)} onSaved={load} />
    </Layout>
  );
}
